import { tint } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';

import {
  MessageType,
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { rem } from '../../../helpers';
import {
  getCustomComponentProperty,
  styledGetCustomComponentProperty,
} from '../../../helpers/theme';

import {
  getColor,
  getHoverColor,
  getTabBlockPadding,
} from './TabBlock.helpers';

interface TabBlockTheming {
  $success: boolean;
  $alert: boolean;
  $error: boolean;
  $info: boolean;
  $isFluid: boolean;
  $componentThemeName?: string;
  $didactic?: string | MessageType;
  $isActive: boolean;
  $isDisabled: boolean;
}

interface MixinSvgProps {
  $didactic?: MessageType | string;
  $info: boolean;
  $success: boolean;
  $alert: boolean;
  $error: boolean;
  $componentThemeName?: string;
}

const mixinSvg = css<MixinSvgProps>`
  & svg {
    color: ${getColor};
    flex-shrink: 0;
    max-height: ${({ $didactic }) => ($didactic ? rem(30) : rem(20))};
    max-width: ${({ $didactic }) => ($didactic ? rem(30) : rem(20))};
    margin-right: ${({ theme, $componentThemeName }) =>
      getCustomComponentProperty({
        theme,
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.SPACE_BETWEEN_ICON_AND_TEXT,
        fallback: getCustomComponentProperty({
          theme,
          componentName: SUPPORTED_COMPONENTS.DEFAULT_BUTTONS,
          property: SUPPORTED_PROPERTIES.SPACE_BETWEEN_ICON_AND_TEXT,
        }),
      })};
  }
`;

export const $Label = styled.div`
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  font-size: ${({ theme }) => theme.fonts.size.medium};
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

export const $Didactic = styled.div`
  font-size: ${({ theme }) => theme.fonts.size.ultraSmall};
  font-weight: ${({ theme }) => theme.fonts.weight.normal};
  text-align: left;
`;

export const $LabelAndIcon = styled.span`
  display: flex;
  align-items: center;
`;

export const $TabBlock = styled.button<TabBlockTheming>`
  max-width: ${({ $isFluid }) => ($isFluid ? null : rem(200))};
  min-width: ${rem(100)};
  overflow: hidden;
  background-color: ${(props) => {
    if (props.$isActive) {
      return getHoverColor(props);
    }
    const color = getColor(props);

    return tint(0.85, color);
  }};
  border-radius: ${({ theme }) => theme.borders.radius};
  display: flex;
  justify-content: center;
  min-height: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
    property: SUPPORTED_PROPERTIES.HEIGHT,
  })};
  border-width: 0;
  border-color: ${getColor};
  border-style: solid;
  border-bottom-width: ${({ $isActive }) => ($isActive ? rem(3) : 0)};
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  padding: ${getTabBlockPadding};
  ${mixinSvg};

  &:focus,
  &:hover {
    background-color: ${getHoverColor};
  }
  transition-property: background-color;
  transition-duration: 0.3s;

  outline: 0;
`;

export const $CustomIcon = styled(({ icon, ...props }) =>
  React.cloneElement(icon, props),
)`
  svg {
    width: 18px;
    height: 18px;
    color: ${({ color }) => {
      return color;
    }};
  }
`;
